import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { useLanguage } from "@/i18n/LanguageContext";
import { Mic, Activity, Volume2, BookOpen } from "lucide-react";

export function DeviceSettingsCard({ audioDevices, selectedDevice, setSelectedDevice }) {
  const { t } = useLanguage();

  return (
    <Card className="col-span-12 md:col-span-6 bento-card" data-testid="device-settings-card">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2 text-white font-['Outfit']">
          <Mic className="w-5 h-5" />
          {t("audioDevice")}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="audio-device" className="text-zinc-400">{t("selectAudioSource")}</Label>
          <Select 
            value={selectedDevice} 
            onValueChange={setSelectedDevice}
            data-testid="audio-device-select"
          >
            <SelectTrigger 
              id="audio-device"
              className="bg-zinc-900 border-zinc-700 text-white"
              data-testid="audio-device-trigger"
              aria-label={t("selectAudioSource")}
            >
              <SelectValue placeholder={t("selectAudioSource")} />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-700">
              {audioDevices.map((device) => (
                <SelectItem 
                  key={device.deviceId} 
                  value={device.deviceId}
                  className="text-white hover:bg-zinc-800"
                >
                  {device.label || `Dispositivo ${device.deviceId.slice(0, 8)}`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <p className="text-zinc-500 text-sm">
          {t("audioDeviceDescription")}
        </p>
      </CardContent>
    </Card>
  );
}

export function DelaySettingsCard({ delayMs, setDelayMs }) {
  const { t } = useLanguage();

  return (
    <Card className="col-span-12 md:col-span-6 bento-card" data-testid="delay-settings-card">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2 text-white font-['Outfit']">
          <Activity className="w-5 h-5" />
          {t("delayAdjustment")}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-4">
          <div className="flex justify-between">
            <Label htmlFor="delay-slider" className="text-zinc-400">{t("translationDelay")}</Label>
            <span className="text-white font-mono" data-testid="delay-value">{delayMs}ms</span>
          </div>
          <Slider
            id="delay-slider"
            value={[delayMs]}
            onValueChange={(value) => setDelayMs(value[0])}
            min={0}
            max={3000}
            step={100}
            className="w-full"
            data-testid="delay-slider"
            aria-label={t("translationDelay")}
          />
          <div className="flex justify-between text-xs text-zinc-500">
            <span>0ms</span>
            <span>3000ms</span>
          </div>
        </div>
        <p className="text-zinc-500 text-sm">
          {t("delayDescription")}
        </p>
      </CardContent>
    </Card>
  );
}

export function GateSettingsCard({ gateThreshold, setGateThreshold }) {
  const { t } = useLanguage();

  return (
    <Card className="col-span-12 md:col-span-6 bento-card" data-testid="gate-settings-card">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2 text-white font-['Outfit']">
          <Volume2 className="w-5 h-5" />
          {t("audioGate")}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-4">
          <div className="flex justify-between">
            <Label htmlFor="gate-slider" className="text-zinc-400">{t("gateThreshold")}</Label>
            <span className="text-white font-mono" data-testid="gate-value">{gateThreshold}dB</span>
          </div>
          <Slider
            id="gate-slider"
            value={[gateThreshold]}
            onValueChange={(value) => setGateThreshold(value[0])}
            min={-60}
            max={0}
            step={1}
            className="w-full"
            data-testid="gate-slider"
            aria-label={t("gateThreshold")}
          />
          <div className="flex justify-between text-xs text-zinc-500">
            <span>-60dB ({t("sensitive")})</span>
            <span>0dB ({t("loud")})</span>
          </div>
        </div>
        <p className="text-zinc-500 text-xs leading-snug line-clamp-3">
          {t("gateDescription")} {t("recommended")}
        </p>
      </CardContent>
    </Card>
  );
}

export function ReadingSpeedCard({ readingSpeed, setReadingSpeed }) {
  const { t } = useLanguage();

  const label =
    readingSpeed <= 11
      ? t("readingSpeedSlow")
      : readingSpeed >= 19
      ? t("readingSpeedFast")
      : t("readingSpeedNormal");

  return (
    <Card className="col-span-12 md:col-span-6 bento-card" data-testid="reading-speed-card">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2 text-white font-['Outfit']">
          <BookOpen className="w-5 h-5" />
          {t("readingSpeed")}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-4">
          <div className="flex justify-between">
            <Label htmlFor="reading-speed-slider" className="text-zinc-400">
              {t("readingSpeedLabel")}
            </Label>
            <span className="text-white font-mono" data-testid="reading-speed-value">
              {label} · {readingSpeed} {t("readingSpeedUnit")}
            </span>
          </div>
          <Slider
            id="reading-speed-slider"
            value={[readingSpeed]}
            onValueChange={(value) => setReadingSpeed(value[0])}
            min={10}
            max={22}
            step={1}
            className="w-full"
            data-testid="reading-speed-slider"
            aria-label={t("readingSpeed")}
          />
          <div className="flex justify-between text-xs text-zinc-500">
            <span>{t("readingSpeedSlow")}</span>
            <span>{t("readingSpeedFast")}</span>
          </div>
        </div>
        <p className="text-zinc-500 text-sm">{t("readingSpeedDescription")}</p>
      </CardContent>
    </Card>
  );
}
